import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { primaryInput } from "detect-it";
import { faArrowRight, faTrashCan, faReply } from "@fortawesome/pro-solid-svg-icons";
import { FaIcon } from "@shared/client/components/fa-icon";
import { showToast } from "@shared/client/components/toast/show-toast";
import { useTextRangeRects } from "../use-text-range-rects";
import { useAccountIdentity } from "@/hooks/use-account-identity";
import { useCurrentAccountId } from "@/hooks/use-current-account-id";
import { userAnnotationStore } from "@/data/user-annotation-store";
import { useAnnotationHoverStore } from "@/data/annotation-hover-store";
import { formatRelativeTime } from "@/lib/format-relative-time";
import { CommentAuthorAvatar } from "./comment-author-avatar";
// Tap-to-popover is a touch-only affordance. On desktop (mouse-primary
// devices, including hybrid laptops with a touchscreen) the comments
// rail is the read path: the rail note already paints the body
// alongside the panel, and hovering the highlight rect lights up the
// matching note via `useAnnotationHoverStore` — so a click-to-open
// popover would be a redundant third surface for the same data.
//
// Detected once at module load via `detect-it`. `primaryInput` reads
// the user-agent's primary pointer: phones and tablets resolve to
// `"touch"`; desktops, laptops, and laptop+touchscreen hybrids resolve
// to `"mouse"`. Static (not a hook) because the input model doesn't
// change inside a session — same pattern as `use-long-press.ts`.
const IS_TOUCH_PRIMARY = primaryInput === "touch";
export const CommentAnnotationView = memo(function CommentAnnotationView(_props) {
    return null;
});
export const CommentTextRangeView = memo(function CommentTextRangeView({ annotation, mode, containerRef, }) {
    // Anchor is narrowed by the parent; assert here so TS picks it up
    // without a runtime branch.
    const anchor = annotation.anchor;
    const rects = useTextRangeRects({
        containerRef,
        mode,
        start: anchor.start,
        end: anchor.end,
    });
    // Look for a sibling `text_highlight` anchored to the exact same
    // text range. When one exists we suppress this comment's own
    // yellow inline highlight: the text_highlight already paints in
    // whichever color the author picked, and double-painting would
    // composite the two via `mix-blend-mode: multiply` into a third,
    // unintended color. The rects themselves stay in the DOM (just
    // transparent) so they remain a tap target for the popover.
    //
    // This is a per-comment subscription, but the underlying TanStack
    // DB live query is deduplicated across consumers — every comment
    // / highlight on the same block reads the same in-memory cursor.
    const { data: siblings } = userAnnotationStore.useByBlock(annotation.blockId);
    const hasSiblingHighlight = useMemo(() => {
        for (const s of siblings) {
            if (s.type !== "text_highlight")
                continue;
            if (s.anchor?.type !== "text_range")
                continue;
            if (s.anchor.start !== anchor.start)
                continue;
            if (s.anchor.end !== anchor.end)
                continue;
            return true;
        }
        return false;
    }, [siblings, anchor.start, anchor.end]);
    // Reply children of THIS comment. Filter siblings down to the
    // `comment` rows whose `parentId` points at us, sorted by
    // `createdAt` ascending so the most recent reply ends up adjacent
    // to the reply input below the thread. Memoized on the siblings
    // reference so the popover re-renders only when the underlying
    // collection mutates, not on every parent re-render.
    const replies = useMemo(() => {
        const parentIdStr = annotation.id;
        const out = [];
        for (const s of siblings) {
            if (s.type !== "comment")
                continue;
            if (!s.parentId)
                continue;
            if (s.parentId !== parentIdStr)
                continue;
            out.push(s);
        }
        out.sort((a, b) => a.createdAt < b.createdAt ? -1 : a.createdAt > b.createdAt ? 1 : 0);
        return out;
    }, [siblings, annotation.id]);
    // Index of the rect the popover is anchored to. `undefined` =
    // closed. We anchor to a specific rect (rather than the union of
    // all rects) so wrapping highlights — which can produce one
    // multi-line highlight with N rects — pop up the body next to
    // whichever line the user actually tapped, not always at the top.
    const [popoverIndex, setPopoverIndex] = useState(undefined);
    const handleRectClick = useCallback((e, idx) => {
        // Block bubbling so the modal's backdrop click-to-close
        // doesn't fire (the rect sits inside the panel, but a tap on
        // the highlight also reads as a tap on the modal's overlay
        // layer above the panel content).
        e.stopPropagation();
        setPopoverIndex((prev) => (prev === idx ? undefined : idx));
    }, []);
    // Dismiss on outside click. Listening on the document at the
    // capture phase so we run before the rect's own click handler
    // (which would otherwise re-toggle the popover if the user taps
    // a different rect of the same highlight). Skipping events whose
    // target is the popover itself keeps the body interactable.
    useEffect(() => {
        if (popoverIndex === undefined)
            return;
        function onPointerDown(e) {
            const target = e.target;
            if (!(target instanceof Element)) {
                setPopoverIndex(undefined);
                return;
            }
            if (target.closest(".annotation-comment-popover"))
                return;
            if (target.closest(`[data-annotation-id="${annotation.id}"]`))
                return;
            setPopoverIndex(undefined);
        }
        document.addEventListener("pointerdown", onPointerDown, { capture: true });
        return () => {
            document.removeEventListener("pointerdown", onPointerDown, {
                capture: true,
            });
        };
    }, [annotation.id, popoverIndex]);
    // Stream cards (mode="regular") are decorative — the card itself
    // is the tap target, so we don't want hover or popover surfaces
    // swallowing the expand-on-tap. Disable rect interactions there
    // entirely.
    const interactive = mode === "expanded";
    // Popover is only a thing on touch-primary devices. Desktop reads
    // the body via the comments rail (see file-level comment), so we
    // skip wiring the click handler entirely there — the rect stays a
    // hover-link target for the rail but no longer responds to clicks.
    const popoverEnabled = interactive && IS_TOUCH_PRIMARY;
    // Hover-link is bidirectional: in addition to the rect publishing
    // its hover state for the rail (below), the rect ALSO subscribes
    // to the same store so a hover on the rail note in the gutter
    // lights up the matching rect inside the panel. Same single-slot
    // store; the rect just reads its own slot.
    //
    // Selector returns a boolean so this view only re-renders when its
    // own hover state flips, not when *some other* comment's hover
    // changes.
    const isLinkedHover = useAnnotationHoverStore((s) => s.hoveredAnnotationId === annotation.id);
    // Resolve the comment author's avatar from the shared attribution
    // store. Returns placeholder initials when the store hasn't been
    // primed for this account — better to show a tan circle with an
    // initial than to silently drop the surface.
    const authorIdentity = useAccountIdentity(annotation.ownerId);
    // Hover-link feedback: while a rect is hovered, publish this
    // comment's id + ownerId to the shared store. The id drives the
    // matching rail note's grow-and-fade-to-full-opacity effect; the
    // ownerId drives the right-rail author avatars' dimming so the
    // creator's avatar pops while every other contributor's fades.
    // We don't subscribe to the store here — only produce. The `mode`
    // gate keeps stream-card hovers from cluing the rail (which only
    // renders inside the modal anyway), but we still produce in
    // regular mode because the avatars rail paints in both modes and
    // benefits from the dim feedback there too.
    const setHoveredAnnotation = useAnnotationHoverStore((s) => s.setHoveredAnnotation);
    const handleRectMouseEnter = useCallback(() => {
        setHoveredAnnotation({
            id: annotation.id,
            ownerId: annotation.ownerId,
            blockId: annotation.blockId,
        });
    }, [
        annotation.id,
        annotation.ownerId,
        annotation.blockId,
        setHoveredAnnotation,
    ]);
    const handleRectMouseLeave = useCallback(() => {
        // Idempotent — Zustand collapses no-op writes, and the producer
        // doesn't need to know whether anyone else has since claimed the
        // hover slot. A fast sweep across two adjacent rects fires
        // leave-then-enter in pointer order, so the next enter overwrites
        // this clear before any subscriber paints.
        setHoveredAnnotation(undefined);
    }, [setHoveredAnnotation]);
    // If the component unmounts mid-hover (e.g. annotation deleted
    // while the cursor is still on top of it), the consumer view of
    // `hoveredAnnotationId` would otherwise stick at this id forever.
    // Clear on teardown.
    useEffect(() => {
        return () => {
            const current = useAnnotationHoverStore.getState().hoveredAnnotationId;
            if (current === annotation.id) {
                useAnnotationHoverStore.getState().setHoveredAnnotation(undefined);
            }
        };
    }, [annotation.id]);
    // Reset the popover whenever the highlight rects shift to a
    // different anchor index count (e.g. content reflowed and a
    // wrapping highlight collapsed to a single rect). Avoids holding
    // a stale `popoverIndex` past the end of the new rects array.
    useEffect(() => {
        if (popoverIndex === undefined)
            return;
        if (popoverIndex >= rects.length)
            setPopoverIndex(undefined);
    }, [popoverIndex, rects.length]);
    if (rects.length === 0)
        return null;
    const popoverRect = popoverIndex !== undefined ? rects[popoverIndex] : undefined;
    // Two siblings (wrapped in a fragment): the rect wrapper carries
    // `overflow: hidden` so per-line rects are clipped to the card's
    // visible area (text past the card's own `overflow: hidden`
    // boundary still has its rects measured at the laid-out
    // position — without clipping, those rects paint in the gutter
    // between cards). The popover lives OUTSIDE the rect wrapper so
    // its body — anchored just below the highlight — doesn't get
    // clipped along with the rects.
    return (_jsxs(_Fragment, { children: [_jsx("div", { className: `annotation-comment-inline annotation-comment-inline--mode-${mode}`, "data-annotation-id": annotation.id, "aria-hidden": true, children: rects.map((r, i) => {
                    const rectClassName = [
                        "annotation-comment-inline__rect",
                        hasSiblingHighlight && "annotation-comment-inline__rect--silent",
                        isLinkedHover && "annotation-comment-inline__rect--linked-hover",
                    ]
                        .filter(Boolean)
                        .join(" ");
                    return (_jsx("div", { className: rectClassName, title: annotation.content.body, onClick: popoverEnabled ? (e) => handleRectClick(e, i) : undefined, onMouseEnter: handleRectMouseEnter, onMouseLeave: handleRectMouseLeave, 
                        // useTextRangeRects returns container-VISUAL pixels.
                        // The inline layer is untransformed (`inset: 0` of the
                        // host), so its CSS pixel grid equals the visual grid
                        // one-to-one — no scale compensation needed.
                        style: {
                            top: `${r.top}px`,
                            left: `${r.left}px`,
                            width: `${r.width}px`,
                            height: `${r.height}px`,
                        } }, i));
                }) }), popoverRect && (_jsx(CommentThreadPopover, { parent: annotation, replies: replies, parentAuthorIdentity: authorIdentity, popoverRect: popoverRect }))] }));
});
const CommentThreadPopover = memo(function CommentThreadPopover({ parent, replies, parentAuthorIdentity, popoverRect, }) {
    // Reply input toggle. Same lifecycle as the rail's reply input:
    // parent renders this open/closed; the input self-focuses on mount
    // and self-clears on submit.
    const [isReplyOpen, setIsReplyOpen] = useState(false);
    const handleReplyToggle = useCallback(() => {
        setIsReplyOpen((open) => !open);
    }, []);
    const handleReplyClose = useCallback(() => {
        setIsReplyOpen(false);
    }, []);
    return (_jsxs("div", { className: "annotation-comment-popover", "data-annotation-id": parent.id, style: {
            // Anchor just below the tapped rect. `top` is the line's
            // top + the line's height so the popover sits *below* the
            // highlighted line. `left` lines up with the rect's left
            // edge — long popovers self-clamp via CSS `max-width`
            // rather than tracking the rect's right edge.
            //
            // popoverRect coords are inline-layer-VISUAL pixels. The
            // inline layer is untransformed, so its CSS pixel grid
            // equals the visual grid one-to-one — no scale
            // compensation needed.
            top: `${popoverRect.top + popoverRect.height + 4}px`,
            left: `${popoverRect.left}px`,
        }, role: "dialog", "aria-label": "Comment thread", children: [_jsx(CommentThreadRow, { comment: parent, authorIdentity: parentAuthorIdentity, showReplyAffordance: true, onToggleReply: handleReplyToggle }), replies.length > 0 && (_jsx("div", { className: "annotation-comment-popover__replies", children: replies.map((reply) => (_jsx(CommentThreadReplyRow, { comment: reply }, reply.id))) })), isReplyOpen && (_jsx(PopoverReplyInput, { parent: parent, onClose: handleReplyClose }))] }));
});
const CommentThreadRow = memo(function CommentThreadRow({ comment, authorIdentity, showReplyAffordance, onToggleReply, }) {
    const currentAccountId = useCurrentAccountId();
    const isOwner = currentAccountId !== undefined &&
        currentAccountId === comment.ownerId;
    // The popover only mounts on top-level rows (parent comments) —
    // replies have their own `CommentThreadReplyRow`. So this row's
    // delete always cascades to any sibling `text_highlight` on the
    // same anchor. See `removeCommentWithCascade` for the scope rule.
    const handleDelete = useCallback(() => {
        void userAnnotationStore
            .removeCommentWithCascade(comment.id)
            .catch(() => {
            showToast("Failed to delete comment");
        });
    }, [comment.id]);
    // Same relative-time treatment as the rail (see the equivalent in
    // `block-detail-comments-rail.tsx`). The two surfaces are alternative
    // read paths for the same data picked by viewport width, so they
    // need to surface the same metadata or comments will feel like
    // different things across devices. Time lives at the row's TRUE
    // bottom-right corner via a 2-row grid layout — see the SCSS notes
    // on `.annotation-comment-popover__row`.
    const timeLabel = formatRelativeTime(comment.createdAt);
    return (_jsxs("div", { className: "annotation-comment-popover__row", children: [_jsx(CommentAuthorAvatar, { identity: authorIdentity }), _jsx("div", { className: "annotation-comment-popover__body", children: comment.content.body }), _jsxs("div", { className: "annotation-comment-popover__actions", children: [showReplyAffordance && onToggleReply && (_jsx("button", { type: "button", className: "annotation-comment-popover__action", onClick: onToggleReply, "aria-label": "Reply to comment", title: "Reply", children: _jsx(FaIcon, { icon: faReply }) })), isOwner && (_jsx("button", { type: "button", className: "annotation-comment-popover__action annotation-comment-popover__action--danger", onClick: handleDelete, "aria-label": "Delete comment", title: "Delete", children: _jsx(FaIcon, { icon: faTrashCan }) }))] }), timeLabel && (_jsx("span", { className: "annotation-comment-popover__time", title: comment.createdAt, children: timeLabel }))] }));
});
const CommentThreadReplyRow = memo(function CommentThreadReplyRow({ comment, }) {
    // Reply rows resolve their own author identity (each reply may have
    // a different author than the parent). Same hook the parent row
    // uses, just hoisted into a dedicated component so the reply
    // chrome can apply its visual downgrade (smaller avatar, dimmer
    // body) via SCSS class without juggling props.
    const authorIdentity = useAccountIdentity(comment.ownerId);
    const currentAccountId = useCurrentAccountId();
    const isOwner = currentAccountId !== undefined &&
        currentAccountId === comment.ownerId;
    const handleDelete = useCallback(() => {
        void userAnnotationStore.remove(comment.id).catch(() => {
            showToast("Failed to delete reply");
        });
    }, [comment.id]);
    const timeLabel = formatRelativeTime(comment.createdAt);
    return (_jsxs("div", { className: "annotation-comment-popover__row annotation-comment-popover__row--reply", children: [_jsx(CommentAuthorAvatar, { identity: authorIdentity }), _jsx("div", { className: "annotation-comment-popover__body", children: comment.content.body }), isOwner && (_jsx("div", { className: "annotation-comment-popover__actions", children: _jsx("button", { type: "button", className: "annotation-comment-popover__action annotation-comment-popover__action--danger", onClick: handleDelete, "aria-label": "Delete reply", title: "Delete", children: _jsx(FaIcon, { icon: faTrashCan }) }) })), timeLabel && (_jsx("span", { className: "annotation-comment-popover__time", title: comment.createdAt, children: timeLabel }))] }));
});
// ---------------------------------------------------------------------------
// PopoverReplyInput — collapsible reply textarea + send button at the
// bottom of the popover. Mirrors the rail's `ReplyInput` (same
// keyboard model: Enter sends, Shift+Enter newlines, Escape closes)
// but uses popover-specific SCSS so the chrome reads as a
// continuation of the popover surface, not a transplanted toolbar.
// ---------------------------------------------------------------------------
const PopoverReplyInput = memo(function PopoverReplyInput({ parent, onClose, }) {
    const [draft, setDraft] = useState("");
    const [isSending, setIsSending] = useState(false);
    const textareaRef = useRef(null);
    useEffect(() => {
        textareaRef.current?.focus();
    }, []);
    const handleSubmit = useCallback(async () => {
        const trimmed = draft.trim();
        if (trimmed.length === 0)
            return;
        if (isSending)
            return;
        setIsSending(true);
        try {
            await userAnnotationStore.create({
                type: "comment",
                blockId: parent.blockId,
                content: { body: trimmed },
                anchor: parent.anchor,
                parentId: parent.id,
            });
            setDraft("");
            onClose();
        }
        catch {
            showToast("Failed to send reply");
        }
        finally {
            setIsSending(false);
        }
    }, [
        draft,
        isSending,
        onClose,
        parent.anchor,
        parent.blockId,
        parent.id,
    ]);
    const handleKeyDown = useCallback((e) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            void handleSubmit();
            return;
        }
        if (e.key === "Escape") {
            e.preventDefault();
            onClose();
        }
    }, [handleSubmit, onClose]);
    return (_jsxs("div", { className: "annotation-comment-popover__reply-input", children: [_jsx("textarea", { ref: textareaRef, className: "annotation-comment-popover__reply-textarea", value: draft, onChange: (e) => setDraft(e.target.value), onKeyDown: handleKeyDown, placeholder: "Reply\u2026", rows: 1, disabled: isSending }), _jsx("button", { type: "button", className: "annotation-comment-popover__reply-send", onClick: () => void handleSubmit(), disabled: isSending || draft.trim().length === 0, "aria-label": "Send reply", title: "Send", children: _jsx(FaIcon, { icon: faArrowRight }) })] }));
});
